import type {PureAbility, ExtractSubjectType} from "@casl/ability"
import {AbilityBuilder, createMongoAbility} from "@casl/ability"
import {MembershipRole, PictureState, SystemRole, TagType} from "#generated"
import {authorizationContext} from "#auth/context"
import type {AuthorizationSilo, AuthRulesUser, ColormassAbility, ColormassAbilitySubject} from "#auth/types"
import {conveyMembershipRights} from "#auth/membership"

export const abilitiesForSilo = (silo: AuthorizationSilo | null | undefined, user: AuthRulesUser | null | undefined): PureAbility => {
    const {isStaff, isSuperuser, isEnterpriseCloud, ownOrganizationIds} = authorizationContext(silo, user)

    const builder = new AbilityBuilder<ColormassAbility>(createMongoAbility)
    const {can: allow, cannot: forbid, build} = builder

    allow("delete", "Picture", {state: PictureState.Draft})
    allow("delete", "TaskComment", {createdBy: {id: user?.id}})

    allow("read", "Organization", {visibleInFilters: true})

    allow("read", "DataObject")

    allow("read", "Material", "files", {public: true})
    allow("read", "Material", ["outputs", "textures"], {public: true, publicDownloads: true})
    allow("download", "Material", {public: true, publicDownloads: true})

    allow("read", "Page", ["models", "materials", "assets", "pictures", "templates"])
    allow("read", "Menu", ["batchDownloadPictures", "materials", "templates"])

    if (user?.memberships && user.memberships.length > 1) {
        allow("read", "Menu", ["authenticateForOrganization"])
    }

    if (isStaff) {
        allow("copy", "Material")
        allow("copy", "Picture")
        allow("copy", "Template")

        allow("create", "all", "batch")
        allow("create", "DataObject")
        allow("create", "ScanJob", "testImage")
        allow("create", "MaterialAssignment")
        allow("create", "MaterialRevision")
        allow("create", "Tag")
        allow("create", "Task")
        allow("create", "Template")
        allow("create", "UserAssignment")
        allow("create", "TextureEditor", "experimentalFeatures")

        allow("delete", "DataObject")
        allow("delete", "Asset")
        allow("delete", "Material")
        allow("delete", "MaterialAssignment")
        allow("delete", "MaterialMapsExport")
        allow("delete", "Model")
        allow("delete", "Organization")
        allow("delete", "Picture")
        allow("delete", "PictureRevision")
        allow("delete", "TaskComment")
        allow("delete", "Template")
        allow("delete", "TemplateRevision")
        allow("delete", "User")

        allow("move", "Picture")

        allow("update", "Asset")
        allow("update", "DataObject")
        allow("update", "Job")
        allow("update", "JobTask")
        allow("update", "Material")
        allow("update", "MaterialRevision")
        allow("update", "Menu")
        allow("update", "Model")
        allow("update", "Organization")
        allow("update", "Picture")
        allow("update", "Project")
        allow("update", "ScanJob", "params")
        allow("update", "Set")
        allow("update", "Tag")
        allow("update", "Task")
        allow("update", "TemplateRevision")
        allow("update", "TextureGroup")
        allow("update", "TextureSet")
        allow("update", "Template", ["organization", "state", "type", "public", "batch"])
        allow("update", "User")

        allow("update", ["Asset", "Material", "Model", "Organization", "Template", "User"], "galleryImage")
        allow("update", ["Asset", "DataObject", "Hdri", "JobTask", "Material", "Model", "Organization", "Picture", "Template"])

        allow("read", "Asset")
        allow("read", "Filters")
        allow("read", "Job", "tiling")
        allow("read", "Tag")
        allow("read", "Task", "public")
        allow("read", "TextureSet")
        allow("read", "all", "paymentState")
        allow("read", "Material")
        allow("download", "Material")
        allow("download", "Template")
        allow("read", "Material", "tags")
        allow("read", "MaterialRevision")
        allow("read", "Menu", [
            "advancedAutoTiling",
            "advancedTemplateEditor",
            "archiveTask",
            "assets",
            "authenticate",
            "authenticateAsSystemRole",
            "authenticateForAnyOrganization",
            "batchDownloadMaterials",
            "batchStartMaterialImageJobs",
            "debugScanJob",
            "extendedCreate",
            "extraFilters",
            "files",
            "filterByOrganization",
            "filterByOrganizationOnTagsPage",
            "generateFabricOverview",
            "goToTemplate",
            "hdris",
            "jobFarmGroups",
            "jobFarmInstances",
            "materialAi",
            "materials",
            "models",
            "narrowScanningDebugMenu",
            "onlineLinks",
            "pictureAssignmentType",
            "products",
            "renamePictureFile",
            "scanning",
            "scenes",
            "tags",
        ])
        allow("read", "Model")
        allow("read", "Organization")
        allow("read", "Page", ["errorsInSnackBar", "pictures", "staffAreas"])
        allow("read", "Picture")
        allow("read", "ScanJob")
        allow("read", "Template", ["files", "templates", "revisions"])

        allow("read", "Page", ["files", "hdris", "jobs", "products", "render-nodes", "scanning", "scenes", "statistics", "tags"])

        allow("read", "TagType", [
            TagType.MaterialRange,
            TagType.PdfSpecItem,
            TagType.PictureAnnotation,
            TagType.ProductCategory,
            TagType.ProductRange,
            TagType.Production,
        ])

        allow("read", "TagsPage", ["all"])
    }

    if (user?.role === SystemRole.Superadmin || user?.role === SystemRole.Staff || user?.isStaff || user?.isSuperuser) {
        allow("read", "Menu", ["authenticate", "authenticateAsStaff", "authenticateAsSelfService", "authenticateForAnyOrganization"])
    }

    if (user?.role === SystemRole.Superadmin || user?.isSuperuser) {
        allow("read", "Menu", ["authenticateAsSuperadmin"])
    }

    if (isSuperuser) {
        allow("create", "TextureEditor", "testOperator")
        allow("create", "TextureEditor", "manualEditMode")
        allow("create", "Material", "overlayColor")
        allow("update", "ScanJob")
        allow("update", "ScanSubJob")
        allow("update", "User", "systemRole")
        allow("read", "Job", "jsonGraph")

        allow("read", "Menu", ["debug", "jobs", "organizationGroups", "organizations", "taskTypes", "users", "statistics"])

        allow("read", "Page")

        allow("read", "InconsistentTemplate")
    }

    const membership = silo?.membership
    if (membership?.organization?.id && membership?.role === MembershipRole.Admin) {
        allow("read", "Organization", {id: membership.organization.id})
        allow("administer", "Organization", {id: membership.organization.id})
        allow("read", "Menu", "organizationSettings")
        allow("create", "User")
        allow("create", "Membership", {"organization.id": membership.organization.id})
    }

    ownOrganizationIds.forEach((organizationId) => {
        conveyMembershipRights(builder, organizationId, isEnterpriseCloud, false)
    })

    // Grant readonly rights for sibling organizations in the same org group.
    // Derived from the silo's organisation, not the user's memberships, so that staff users
    // switching to a customer silo see the same sibling orgs as a real member of that organisation.
    // Note: the organization search dropdown (filterByOrganization menu) is intentionally staff-only;
    // non-staff org-group members filter their siblings via the radio organization chips instead.
    const siblingOrganizationIds =
        silo?.organization?.organizationGroup?.organizations?.map((organization) => organization.id).filter((id) => !ownOrganizationIds.includes(id)) ?? []

    siblingOrganizationIds.forEach((siblingOrganizationId) => conveyMembershipRights(builder, siblingOrganizationId, isEnterpriseCloud, true))

    // Forbids must come after conveyMembershipRights because CASL uses last-rule-wins
    if (!isStaff) {
        forbid("update", "DataObject", "imageColorSpace")
        forbid(["create", "read", "update", "delete"], "all", ["batch", "completion", "defaultJobsPriority", "drawing", "paymentState", "position", "priority"])
        forbid("update", "Model", ["organization"])
        forbid("update", "Material", ["organization", "public", "sampleArrival", "type", "usage"])
        forbid("update", "Template", ["organization", "public", "state", "type"])
        forbid("update", "Picture", "state", {state: PictureState.Completed})
        forbid("update", "Picture", "nextActor", {
            state: {$in: [PictureState.Completed, PictureState.Draft, PictureState.OnHold, PictureState.Review]},
        })

        if (!isEnterpriseCloud) {
            allow("read", "Menu", ["assets", "models"])
            forbid("update", "Asset", "state")
            forbid("update", "Material", "state")
            forbid("update", "Model", "state")
            forbid("update", "Picture", "state")
        }
    }

    return build({
        detectSubjectType: (object: {__typename: ExtractSubjectType<ColormassAbilitySubject>}) => object.__typename,
    })
}
